import { useNavigate, useLocation } from "react-router-dom";
import { ExtraCharge } from "../../components/molecules/extraCharge/ExtraCharge";
import { useExtraCharges } from "../../hooks/useExtraCharges/useExtraCharges";
import { UseGetStoreData } from "../../hooks/getStoreData/UseGetStoreData";
import { calculateFareApi, getOrdersByTicketId, refreshPaymentStatus } from "../../http";
import { useEffect, useState } from "react";
import { useModal } from "../../components/organisms/modalProvider/ModalProvider";
import { ExtraChargePaymentLink } from "../../components/molecules/extraChargePaymentLink/ExtraChargePaymentLink";
import { Badge, Button as AntBtn, Card } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useSseTrigger } from "../../hooks/UseSseTrigger/UseSseTrigger";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShowAlert } from "../../utils/showAlert";

export const ExtraChargesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { getTicketData, getTicketStatus } = UseGetStoreData();
  const isCompleted = getTicketStatus === "completed";
  const { extraCharges, setExtraCharges, dropLocations, setDropLocations, handleSelectDropLocation, handleDeleteDropLocation, saveDropLocations, isSaving, distanceData, ambulanceData, hasUnsavedChanges } = useExtraCharges();
  const { showModal } = useModal();
  const { connectSse } = useSseTrigger();
  const [fareData, setFareData] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: ordersData } = useQuery({
    queryKey: ['getOrdersByTicketId-extraCharges', getTicketData?._id],
    queryFn: () => getOrdersByTicketId(getTicketData._id),
    enabled: !!getTicketData?._id,
  });

  const paymentOrders = ordersData?.data?.filter((order: any) => order.notes?.order_by === "Extra Charges").sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) || [];

  useEffect(() => {
    connectSse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchFare = async () => {
      const provider = ambulanceData?.[0]?.ambulanceProvider;
      if (ambulanceData?.[0]?._id && provider) {
        try {
          const response = await calculateFareApi({
            provider,
            documentId: ambulanceData[0]._id
          });
          setFareData(response.data);
        } catch (error) {
          console.error('Error fetching fare:', error);
        }
      }
    };
    fetchFare();
  }, [ambulanceData]);

  useEffect(() => {
    const fromPage = location.state?.from;
    if (fromPage === 'extraCharges-Drop Location' && location.state?.selectedHospital) {
      const editingIndex = sessionStorage.getItem('extraChargeEditingIndex');
      if (editingIndex !== null) {
        const index = parseInt(editingIndex);
        const hospital = location.state.selectedHospital;
        const newLocations = [...dropLocations];

        const convertedHospital = {
          ...hospital,
          location: {
            ...hospital.location,
            coordinates: [
              hospital.location?.coordinates?.[1] || hospital.location?.latitude,
              hospital.location?.coordinates?.[0] || hospital.location?.longitude
            ]
          }
        };

        newLocations[index] = convertedHospital;
        setDropLocations(newLocations);
        sessionStorage.removeItem('extraChargeEditingIndex');
      }
    }
  }, [location.state, setDropLocations, dropLocations]);

  const handleSave = async () => {
    await saveDropLocations(dropLocations);
  };

  const handleSendPaymentLink = (totalAmount: number) => {
    showModal(<ExtraChargePaymentLink totalAmount={totalAmount} />, "Ambulance Payment");
  };

  const handleRefreshPaymentStatus = async () => {
    if (!getTicketData?._id) return;
    setIsRefreshing(true);
    try {
      await refreshPaymentStatus(getTicketData._id);
      await queryClient.invalidateQueries({
        queryKey: ['getOrdersByTicketId-extraCharges', getTicketData._id],
        exact: true,
        refetchType: 'all',
      });
      ShowAlert("success", "Payment status refreshed successfully");
    } catch (e: any) {
      ShowAlert("warning", e?.response?.data?.message || "Failed to refresh payment status");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 600, margin: 0 }}>Extra Charges</h2>
        <button
          onClick={() => navigate('/doctor-consult-ambulance')}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#f0f0f0",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px"
          }}
        >
          Back
        </button>
      </div>

      <div style={{ position: "relative", pointerEvents: isCompleted ? "none" : "auto", opacity: isCompleted ? 0.5 : 1 }}>
        <ExtraCharge
          extraCharges={extraCharges}
          setExtraCharges={setExtraCharges}
          dropLocations={dropLocations}
          setDropLocations={setDropLocations}
          onSelectDropLocation={handleSelectDropLocation}
          onDeleteDropLocation={handleDeleteDropLocation}
          onSave={handleSave}
          isSaving={isSaving}
          onSendPaymentLink={handleSendPaymentLink}
          distanceData={distanceData}
          ticketId={getTicketData?.ticketId || getTicketData?._id}
          fareData={fareData}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      </div>

      {paymentOrders.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ fontSize: "18px", fontWeight: 600, margin: 0 }}>Payment History</h3>
            {paymentOrders.some((order: any) => order.payment_status !== "paid" && order.payment_status !== "cancelled" && order.payment_status !== "expired") && (
              <AntBtn
                icon={<ReloadOutlined spin={isRefreshing} />}
                onClick={handleRefreshPaymentStatus}
                loading={isRefreshing}
                disabled={isCompleted}
              >
                Refresh Payment Status
              </AntBtn>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {paymentOrders.map((order: any, index: number) => (
              <Badge.Ribbon
                key={index}
                style={{ backgroundColor: order.payment_status === "paid" ? "green" : order.payment_status === "failed" ? "red" : "orange" }}
                text={order.payment_status === "paid" ? "Paid" : order.payment_status === "failed" ? "Failed" : "Created"}
              >
                <Card size="small">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{order.notes?.order_by}</div>
                      <div style={{ fontSize: "14px", color: "#666" }}>
                        {order.beneficiary?.name} - ₹{order.amount}
                      </div>
                    </div>
                  </div>
                </Card>
              </Badge.Ribbon>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
